import { MerchantLocation } from '../../api/types';
import { StepId } from '../constants';

export type ResolveStatus =
  | 'pending'
  | 'existing'
  | 'new'
  | 'conflict'
  | 'error';

export type ResolveReason = 'NotFound' | 'Conflict' | 'Terminated';

export interface ResolveResult<TData> {
  status: ResolveStatus;
  data?: TData;
  reason?: ResolveReason;
  conflictFields?: Array<{
    field: string;
    incoming: unknown;
    existing: unknown;
  }>;
  errorMessage?: string;
}

export interface ContractInputProcedureCode {
  code: string;
  description: string;
}

// One row inside a "Global Fees by Scan Type" fee schedule. Surfaced in the
// fee-schedule step as a read-only reference table — the wizard does not
// resolve these descriptions to KG entities or validate them against the
// operator's composed lines.
export interface ContractInputModalityFee {
  description: string;
  global_fee: number;
}


// Structural check: a non-empty `services` map (at least one row in at least
// one bucket) marks the modality flavor. The `type` field is informational
// only — the wizard must not branch on its string value.
//
// "Non-empty" matters because intake is now lenient: a fee schedule with a
// `services` key but no rows used to be rejected up-front; now it slips
// through and would otherwise land the operator in the modality flow with no
// reference rows. Treating that case as "not modality" pushes it into
// `unstructured` instead, symmetric with the case-rate side.
export const isGlobalByModalityFeeSchedule = (
  feeSchedule:
    | { services?: Record<string, unknown> | null }
    | undefined,
): boolean => {
  const services = feeSchedule?.services;
  if (services == null || typeof services !== 'object') return false;
  return Object.values(services).some(
    rows => Array.isArray(rows) && rows.length > 0,
  );
};

export type FeeScheduleFlavor = 'case-rate' | 'modality' | 'unstructured';

// Three-way classification used by the fee-schedule wizard step.
//   modality      – non-empty `services` map (one+ rows in one+ buckets)
//   case-rate     – at least one non-empty category under `codes`
//   unstructured  – neither; the operator builds lines from scratch
//
// The classifier is intentionally symmetric: "input has structured content of
// flavor X" beats "input has a key shaped like X but no rows", so a half-empty
// shape never strands the operator in a flavor-specific view with nothing to
// reference.
export const classifyFeeScheduleInput = (
  feeSchedule:
    | {
        codes?: {
          category_a?: unknown;
          category_b?: unknown;
          category_c_excluded?: unknown;
        };
        services?: Record<string, unknown> | null;
      }
    | undefined,
): FeeScheduleFlavor => {
  if (isGlobalByModalityFeeSchedule(feeSchedule)) return 'modality';
  const codes = feeSchedule?.codes;
  const hasCaseRows =
    (Array.isArray(codes?.category_a) && codes.category_a.length > 0) ||
    (Array.isArray(codes?.category_b) && codes.category_b.length > 0) ||
    (Array.isArray(codes?.category_c_excluded) &&
      codes.category_c_excluded.length > 0);
  return hasCaseRows ? 'case-rate' : 'unstructured';
};

export interface ContractInputPractitioner {
  name: string;
  npi: string | null;
}

export interface ContractInputAddress {
  address_line_1: string;
  address_line_2?: string;
  locality: string;
  region_code: string;
  country_code: string;
  postal_code: string;
}

export interface ContractInputLocation {
  address: ContractInputAddress;
  phone?: string;
  fax?: string;
  claims_payment_address?: string;
  // Identifiers live per-location: the same legal entity can operate multiple
  // locations under different (tax_id, group_npi_type2) pairs, and customer
  // contract JSON lists them at location level. PR 1 of the per-location
  // migration enforces a single tax_id across the whole input — the multi-org
  // fan-out (one Contract per distinct tax_id) is PR 2.
  tax_id: string;
  group_npi_type2: string;
  medicare_number?: string;
  practitioners: ContractInputPractitioner[];
}

export interface ContractInput {
  client_name: string;
  entity_legal_name: string;
  contract_details: {
    effective_date: string;
    term_end: string;
    renewal?: string;
    termination_notice_standard?: string;
    termination_notice_fee_schedule_dispute?: string;
  };
  fee_schedule: {
    // Free-form display label, e.g. "Global Fees by Scan Type" or "Case Rate".
    // Informational only — the wizard infers flavor from the structural
    // presence of `services` (modality) vs `codes` (case-rate), not from this
    // string.
    type?: string;
    rate_per_case?: number;
    currency: 'USD';
    case_minimum_duration_minutes?: number;
    minimum_category_a_codes?: number;
    pt_engagement_first_case_pct?: number;
    pt_engagement_subsequent_case_pct?: number;
    codes: {
      category_a: ContractInputProcedureCode[];
      category_b: ContractInputProcedureCode[];
      category_c_excluded: ContractInputProcedureCode[];
    };
    // Derived on save from the parent_modality of each row's resolved Item
    // (per the admin-assistant spec). Accept-but-ignore on read so existing
    // JSON fixtures that include the array still validate.
    covered_modalities?: string[];
    // Modality-flavored fee schedules carry rows here. Bucket keys are
    // open-ended (mri, ct_scan, bone_density, …) and normalized to lowercase
    // snake_case during validation. Presence of this map is what marks the
    // input as the modality flavor.
    services?: Record<string, ContractInputModalityFee[] | undefined>;
  };
  locations: ContractInputLocation[];
}

export interface MerchantHit {
  merchantId: string;
  name: string;
}

export interface MerchantLocationHit {
  merchantLocationId: string;
  name?: string;
  displayName?: string;
}

export interface ProviderIndividualHit {
  providerIndividualId: string;
  name: string;
}

export interface AccessPointHit {
  accessPointId: string;
}

export interface ItemHit {
  itemId: string;
}

export type ConflictDecision = 'reuse' | 'override' | 'skip';

export type CustomerAccountDecision =
  | { mode: 'reuse'; id: string; name: string }
  | { mode: 'create'; name: string; taxId: string };

export type FeeScheduleLineKind = 'bundle' | 'item';

export interface FeeScheduleLineDecision {
  kind: FeeScheduleLineKind;
  refId: string;
  priceDollars: number;
  // Display-only label used in Step-07 / Step-10 (CPT code, bundle name, etc.)
  label: string;
}

export interface FeeScheduleDecision {
  reuseId?: string;
  name: string;
  effectiveStart: string;
  effectiveEnd: string;
  lines: FeeScheduleLineDecision[];
}

export type ContractDecision =
  | {
      mode: 'create';
      name: string;
      effectiveStart: string;
      effectiveEnd: string | null;
      openEnded: boolean;
    }
  | {
      mode: 'reuse';
      existingId: string;
      existingName: string;
      effectiveStart: string;
      effectiveEnd: string | null;
      // Set when the user picks an existing contract whose dates differ from
      // the input dates. Continuing past the contract step requires this to be
      // true so the operator has explicitly accepted the date mismatch.
      datesDifferAcknowledged: boolean;
    };

export interface CspRow {
  id: string;
  included: boolean;
  effectiveStart: string;
  effectiveEnd?: string;
}

export interface CspsDecision {
  mode: 'location' | 'access_point';
  rows: CspRow[];
}

export interface CommitLogEntry {
  step: string;
  status: 'pending' | 'success' | 'error' | 'simulated';
  idempotencyKey: string;
  response?: unknown;
  error?: string;
}

export interface WizardState {
  input: ContractInput | null;
  rawInput: string;
  resolutions: {
    // PR 2 of the multi-org migration: merchant resolution is per-TIN, keyed
    // by tax_id. Single-TIN input has exactly one entry here. The wizard
    // iterates `getOrgGroups(input)` to walk this map.
    merchant: Record<string, ResolveResult<MerchantHit>>;
    merchantLocations: Record<string, ResolveResult<MerchantLocationHit>>;
    // Cached "what does this merchant already own?" list, keyed by tax_id.
    // Populated by the Locations step for each TIN whose Merchant resolved as
    // `existing` (no merchantId means nothing to list). Read-only browse data
    // backing the existing-locations table and the manual-match picker.
    existingMerchantLocations: Record<string, MerchantLocation[]>;
    providerIndividuals: Record<string, ResolveResult<ProviderIndividualHit>>;
    accessPoints: Record<string, ResolveResult<AccessPointHit>>;
    items: Record<string, ResolveResult<ItemHit>>;
  };
  decisions: {
    conflictResolutions: Record<string, ConflictDecision>;
    createAccessPoints: boolean;
    // Per-TIN, keyed by tax_id. Each distinct TIN in the input becomes one
    // Contract; each Contract has its own ProviderAccount/Organization
    // (customerAccount), Contract decision, and FeeSchedule materialized at
    // commit time.
    customerAccount: Record<string, CustomerAccountDecision>;
    feeSchedule: FeeScheduleDecision | null;
    contract: Record<string, ContractDecision>;
    csps: CspsDecision | null;
    // Operator-provided legal entity name per TIN. Seeded from
    // input.entity_legal_name for the first TIN; the operator types the rest
    // during intake when more than one TIN is present.
    orgNames: Record<string, string>;
    // Operator-provided display name per new input location. Keyed by
    // locationKey (`${locality}|${address_line_1}`). Seeded from
    // address.locality; operator may override before commit.
    locationNames: Record<string, string>;
    // Per-row checkbox state for the Locations step. `input` is keyed by
    // locationKey (`${locality}|${address_line_1}`); `existing` is keyed by
    // merchantLocationId. Operator-touched rows are recorded explicitly;
    // anything missing falls back to defaults at read time (input → true,
    // existing → false). Existing rows that overlap a resolved input row are
    // forced-on + disabled in the UI; the overlap is recomputed from
    // resolutions, not stored.
    locationSelection: {
      input: Record<string, boolean>;
      existing: Record<string, boolean>;
    };
  };
  commit: {
    started: boolean;
    completed: boolean;
    log: CommitLogEntry[];
    resumeFromStep: string | null;
    // Bumped each time the user clicks Resume after a failure. Suffixed onto
    // every write idempotency key so the BFF/KG service treats each retry as a
    // fresh request instead of replaying the cached failure.
    resumeAttempt: number;
    // Random short nonce regenerated on each Resume click. Combined with
    // resumeAttempt in the suffix so two different retries can never collide
    // even if their attempt numbers were ever to repeat.
    resumeNonce: string;
    // Operator-controlled kill switch on the Review page. true = walk the
    // plan but log every write step as 'simulated' without calling the BFF.
    // Default true so the safe path is always the unconfirmed default; the
    // Review page requires an explicit confirm dialog to flip to false.
    simulateOnly: boolean;
  };
  furthestUnlockedStep: StepId;
}

export const initialWizardState: WizardState = {
  input: null,
  rawInput: '',
  resolutions: {
    merchant: {},
    merchantLocations: {},
    existingMerchantLocations: {},
    providerIndividuals: {},
    accessPoints: {},
    items: {},
  },
  decisions: {
    conflictResolutions: {},
    createAccessPoints: false,
    customerAccount: {},
    feeSchedule: null,
    contract: {},
    csps: null,
    orgNames: {},
    locationNames: {},
    locationSelection: { input: {}, existing: {} },
  },
  commit: {
    started: false,
    completed: false,
    log: [],
    resumeFromStep: null,
    resumeAttempt: 0,
    resumeNonce: '',
    simulateOnly: true,
  },
  furthestUnlockedStep: 'intake',
};
